import { Navigate, useLocation } from 'react-router-dom';
import useAuth from './useAuth';
import usePermissions from './usePermissions';

/**
 * Gates a route. Three ways to use it:
 *   - no props beyond children → just an auth check (must be signed in).
 *   - roles={[...]}            → must hold one of these Entra roles.
 *   - capability="canSeeSales" → must have this capability flag from
 *                                usePermissions (which honours the per-user
 *                                overrides in accessOverrides.js, so a person
 *                                granted an exception passes even without the
 *                                role).
 * A signed-in user who lacks access is redirected to /dashboard rather than
 * kicked out — a mismatch is almost always a permissions bug, not a security
 * one.
 */
export default function RequireRole({ roles, capability, children }) {
  const { account, hasRole } = useAuth();
  const perms = usePermissions();
  const location = useLocation();

  if (!account) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (capability) {
    return perms[capability] === true ? (
      children
    ) : (
      <Navigate to="/dashboard" replace />
    );
  }

  if (roles && roles.length > 0) {
    const ok = roles.some((r) => hasRole(r));
    if (!ok) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
